import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getBackendService } from '../../shared/services/apiConfig';
import firestoreService from '../../shared/services/firestoreService';
import type { Customer, SegmentRulesDoc } from '../../shared/types';
import {
    type CustomerSegment,
    SEGMENT_LABELS,
    SEGMENT_PALETTE,
    NO_SEGMENT_PALETTE,
    NO_SEGMENT_LABEL,
    PRIMARY_SEGMENT_CARDS,
    ALL_SEGMENTS,
    DEFAULT_SEGMENT_RULES,
    describeSegment,
    segmentLabel,
} from '../../shared/customerSegmentation';
import CustomerAvatar from '../../shared/design/components/CustomerAvatar';
import { Button } from '../../shared/design';
import SegmentRulesEditor from './SegmentRulesEditor';

interface CustomerSegmentsProps {
    onSelectCustomer: (id: string | number) => void;
    showStats?: boolean;
    onCustomersLoaded?: (customers: Customer[]) => void;
}

const PAGE_SIZE = 25;

const errorCode = (e: unknown): string | undefined =>
    typeof e === 'object' && e !== null && 'code' in e ? String((e as { code: unknown }).code) : undefined;

const formatLastVisit = (c: Customer): string => {
    if (c.daysSinceLastVisit == null) return 'Jamais venu';
    if (c.daysSinceLastVisit === 0) return "Aujourd'hui";
    if (c.daysSinceLastVisit === 1) return 'Hier';
    return `Il y a ${c.daysSinceLastVisit} jours`;
};

const formatBasket = (basket: number | null): string => {
    if (basket == null) return '—';
    return `${basket.toFixed(2)} €`;
};

const formatComputedAt = (d: Date | null): string => {
    if (!d) return 'pas encore (premier calcul cette nuit)';
    return d.toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const SegmentBadge: React.FC<{ segment: CustomerSegment | null | undefined }> = ({ segment }) => {
    const palette = segment && SEGMENT_PALETTE[segment] ? SEGMENT_PALETTE[segment] : NO_SEGMENT_PALETTE;
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${palette.bg} ${palette.text} ring-1 ring-inset ${palette.ring}`}>
            {segmentLabel(segment)}
        </span>
    );
};

type SegmentFilter = 'all' | 'none' | CustomerSegment;

const CustomerSegments: React.FC<CustomerSegmentsProps> = ({ onSelectCustomer, showStats = true, onCustomersLoaded }) => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [rulesDoc, setRulesDoc] = useState<SegmentRulesDoc>({ rules: DEFAULT_SEGMENT_RULES, lastComputedAt: null });
    const [restaurantId, setRestaurantId] = useState<string | number | null>(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [filter, setFilter] = useState<SegmentFilter>('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [editorOpen, setEditorOpen] = useState(false);
    const [recomputing, setRecomputing] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);

    // Garde contre les chargements qui se chevauchent ou arrivent après démontage.
    const loadSeq = useRef(0);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true; // re-monté par StrictMode en dev : on repart propre
        return () => { mounted.current = false; };
    }, []);

    const load = useCallback(async () => {
        const seq = ++loadSeq.current;
        setRefreshing(true);
        try {
            const backend = await getBackendService();
            const user = backend.getCurrentUser();
            if (user?.restaurantId && backend.getRestaurantCustomers) {
                const [data, rules] = await Promise.all([
                    backend.getRestaurantCustomers(user.restaurantId),
                    ((backend as any).getSegmentRules
                        ? (backend as any).getSegmentRules(user.restaurantId)
                        : firestoreService.getSegmentRules(user.restaurantId)) as Promise<SegmentRulesDoc>,
                ]);
                if (!mounted.current || seq !== loadSeq.current) return;
                setRestaurantId(user.restaurantId);
                setCustomers(data);
                setRulesDoc(rules);
                onCustomersLoaded?.(data);
            }
        } catch (error) {
            console.error('Error loading customers', error);
        } finally {
            if (mounted.current && seq === loadSeq.current) {
                setInitialLoading(false);
                setRefreshing(false);
            }
        }
    }, [onCustomersLoaded]);

    useEffect(() => { load(); }, [load]);

    // Compteurs par segment — lus depuis le segment calculé serveur, jamais redéfinis ici.
    const segmentCounts = useMemo(() => {
        const counts: Record<CustomerSegment | 'none', number> = {
            nouveau: 0, habitue: 0, vip: 0, gros_panier: 0, a_risque: 0, none: 0,
        };
        for (const c of customers) counts[c.segment && c.segment in SEGMENT_LABELS ? c.segment : 'none']++;
        return counts;
    }, [customers]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return customers.filter(c => {
            if (filter === 'none' && c.segment) return false;
            if (filter !== 'all' && filter !== 'none' && c.segment !== filter) return false;
            if (q && !c.name.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [customers, filter, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * PAGE_SIZE;
    const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

    // Reset page sur changement filtre/recherche
    useEffect(() => { setPage(1); }, [filter, search]);

    const recomputeNow = async () => {
        if (!restaurantId) return;
        setRecomputing(true);
        setNotice(null);
        try {
            const res = await firestoreService.recomputeSegments(restaurantId);
            setNotice(`Segments recalculés : ${res.members} clients, ${res.changed} mis à jour.`);
            await load();
        } catch (e) {
            setNotice(errorCode(e) === 'functions/resource-exhausted'
                ? 'Trop de recalculs cette heure-ci. Réessayez plus tard, le calcul de nuit tourne de toute façon.'
                : 'Recalcul impossible pour le moment.');
            console.error('recomputeSegments', e);
        } finally {
            setRecomputing(false);
        }
    };

    const onRulesSaved = useCallback(async (recomputed: boolean, warning?: string) => {
        setEditorOpen(false);
        setNotice(warning ?? (recomputed
            ? 'Règles enregistrées et segments recalculés.'
            : 'Règles enregistrées. Elles s\'appliqueront au prochain calcul de nuit.'));
        await load();
    }, [load]);

    const closeEditor = useCallback(() => setEditorOpen(false), []);

    if (initialLoading) {
        return <div className="p-8 text-center text-gray-500 dark:text-gray-400 font-medium">Chargement des clients…</div>;
    }

    const rulesUnavailable = rulesDoc.loadFailed === true;

    return (
        <div className={`space-y-6 ${refreshing ? 'opacity-70 transition-opacity' : ''}`} aria-busy={refreshing}>
            {showStats && (
                <>
                    {/* Titre + actions */}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <h1 className="page-title">Clients</h1>
                            <p className="page-subtitle">
                                Segments calculés chaque nuit selon vos règles · dernier calcul : {formatComputedAt(rulesDoc.lastComputedAt)}.
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditorOpen(true)}
                                disabled={rulesUnavailable || !restaurantId}
                            >
                                Règles de segmentation
                            </Button>
                            <Button
                                size="sm"
                                variant="primary"
                                onClick={recomputeNow}
                                loading={recomputing}
                                disabled={recomputing || rulesUnavailable || !restaurantId}
                            >
                                Recalculer maintenant
                            </Button>
                        </div>
                    </div>
                    {rulesUnavailable && (
                        <p role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3">
                            Impossible de lire vos règles de segmentation. Rechargez la page avant de les modifier.
                        </p>
                    )}
                    {notice && (
                        <p role="status" className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-800 rounded-xl px-4 py-3">
                            {notice}
                        </p>
                    )}

                    {/* 5 cartes compteurs — 1 par segment, cliquables comme filtres */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {PRIMARY_SEGMENT_CARDS.map(seg => {
                            const palette = SEGMENT_PALETTE[seg];
                            const active = filter === seg;
                            return (
                                <button
                                    key={seg}
                                    type="button"
                                    onClick={() => setFilter(active ? 'all' : seg)}
                                    className={`text-left rounded-xl p-5 border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary ${
                                        active
                                            ? `${palette.bg} ring-2 ${palette.ring} border-transparent`
                                            : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                                    }`}
                                    aria-pressed={active}
                                >
                                    <span className={`block text-sm font-bold ${palette.text}`}>{SEGMENT_LABELS[seg]}</span>
                                    <span className="block mt-2 text-3xl text-gray-900 dark:text-gray-100 font-stat">
                                        {segmentCounts[seg]}
                                    </span>
                                    <span className="block mt-1.5 text-sm leading-snug text-gray-600 dark:text-gray-400">{describeSegment(seg, rulesDoc.rules)}</span>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Recherche + filtre */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
                <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                    <div className="flex-1 max-w-md">
                        <label htmlFor="customer-search" className="sr-only">Rechercher</label>
                        <div className="relative">
                            <input
                                id="customer-search"
                                type="text"
                                placeholder="Rechercher par nom…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 border border-gray-400 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none"
                            />
                            <svg className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-semibold">Segment</span>
                        <Button size="sm" variant={filter === 'all' ? 'neutral' : 'ghost'} onClick={() => setFilter('all')}>
                            Tous ({customers.length})
                        </Button>
                        {ALL_SEGMENTS.map(seg => (
                            <Button key={seg} size="sm" variant={filter === seg ? 'neutral' : 'ghost'} onClick={() => setFilter(seg)}>
                                {SEGMENT_LABELS[seg]} ({segmentCounts[seg]})
                            </Button>
                        ))}
                        {segmentCounts.none > 0 && (
                            <Button size="sm" variant={filter === 'none' ? 'neutral' : 'ghost'} onClick={() => setFilter('none')}>
                                {NO_SEGMENT_LABEL} ({segmentCounts.none})
                            </Button>
                        )}
                    </div>
                </div>

                {/* Tableau */}
                <div className="overflow-x-auto border-t border-gray-100 dark:border-gray-800">
                    <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                        <thead className="bg-gray-50 dark:bg-gray-800/60">
                            <tr>
                                {['Client', 'Segment', `Visites ${rulesDoc.rules.windowDays} j`, 'Visites', 'Panier moyen', 'Dernière visite'].map(h => (
                                    <th key={h} scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-50 dark:divide-gray-800">
                            {pageItems.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-12 text-center">
                                        <p className="text-gray-500 dark:text-gray-400 font-medium">
                                            {filter === 'all' && !search
                                                ? 'Aucun client pour cet établissement.'
                                                : 'Aucun client dans ce segment.'}
                                        </p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                            {filter !== 'all' || search ? 'Essayez d\'ajuster les filtres.' : 'Les clients apparaîtront dès leur première visite NFC validée.'}
                                        </p>
                                    </td>
                                </tr>
                            ) : pageItems.map(c => (
                                <tr
                                    key={c.id}
                                    onClick={() => onSelectCustomer(c.id)}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer transition-colors"
                                >
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <CustomerAvatar name={c.name} avatarUrl={c.avatarUrl} size="sm" verified />
                                            <button
                                                type="button"
                                                onClick={e => { e.stopPropagation(); onSelectCustomer(c.id); }}
                                                className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-brand-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
                                            >
                                                {c.name}
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <SegmentBadge segment={c.segment} />
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                                        {c.segmentStats ? c.segmentStats.visitsInWindow : '—'}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">{c.totalVisits}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 tabular-nums">{formatBasket(c.averageBasket)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatLastVisit(c)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination (côté client pour MVP) */}
                {filtered.length > PAGE_SIZE && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 rounded-b-xl">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">{pageStart + 1}</span>
                            {' – '}
                            <span className="font-semibold text-gray-700 dark:text-gray-300">{Math.min(pageStart + PAGE_SIZE, filtered.length)}</span>
                            {' sur '}
                            <span className="font-semibold text-gray-700 dark:text-gray-300">{filtered.length}</span>
                        </p>
                        <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}>
                                Précédent
                            </Button>
                            <span className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400">
                                Page {safePage} / {totalPages}
                            </span>
                            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>
                                Suivant
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {editorOpen && restaurantId && (
                <SegmentRulesEditor
                    restaurantId={restaurantId}
                    initialRules={rulesDoc.rules}
                    onClose={closeEditor}
                    onSaved={onRulesSaved}
                />
            )}
        </div>
    );
};

export default CustomerSegments;
